<template>
  <v-app >
    <v-app-bar
      id="tmc-app-bar"
      height="70"
      short app
      dark
      color="primary"
      shrink-on-scroll
      prominent
      src="@/assets/OklahomaLogo.jpg"
      fade-img-on-scroll
      class="app-bar-class"
      v-if="!reportMode && !isPrintScreen"
    >
      <template v-slot:img="{ props }">
        <v-img
          v-bind="props"
          gradient="to top right, rgba(25,118,210,.7), rgba(25,32,72,.7)"
        ></v-img>
      </template>
      <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
<!-- Added Global Title -->
      <v-spacer></v-spacer>
      <div class="global-title">{{globalTitle}}</div>

      <v-spacer></v-spacer>
      <div class="d-flex flex-column align-end mr-2">
        <div class="d-flex align-center">
          <span id="spanRouteName" class="mr-2">{{this.$route.name}}</span>
          <div class="workstation-number">Wkst-{{wkst}}</div>
        </div>
        <div
          class="header-years"
          v-if="headerTaxYear || headerFiscalYear"
        >
          <span v-if="headerTaxYear" class="mr-4">Tax Year: {{headerTaxYear}}</span>
          <span v-if="headerFiscalYear">Fiscal Year: {{headerFiscalYear}}</span>
        </div>
      </div>
      <NotificationBadge/>

    </v-app-bar>
    <NotificationSnackbar/>
    <ReAuth />
    <UpdateUserPassword />
    <InputDialogForWorkstationId />
    <v-main>
      <router-view />
    </v-main>
    <v-navigation-drawer
      v-model="drawer"
      fixed
      bottom
      temporary
    >
      <ProgramList />
    </v-navigation-drawer>
    <Snackbar />
    <Footer
      v-if="!reportMode && !isPrintScreen"
      @printScreen="printScreen"
    />
    <ScreenshotFab
      v-if="globalSettingItem && globalSettingItem.enableTmSupportOption"
    />
  </v-app>
</template>

<script>
import {
  mapState,
  mapActions,
  mapMutations,
} from 'vuex';

import {
  toFiscalYear,
  toIsoDate,
  getBusinessDateForPaymentEntry,
} from './util/shared/tmc-global';
import ProgramList from './components/common/ProgramList.vue';
import ReAuth from './components/common/ReAuth.vue';
import Snackbar from './components/common/Snackbar.vue';
import Footer from './components/common/Footer.vue';
import UpdateUserPassword from './components/common/UpdateUserPassword.vue';
import InputDialogForWorkstationId from './components/common/InputDialogForWorkstationId.vue';
import NotificationBadge from './components/common/announcement/NotificationBadge.vue';
import NotificationSnackbar from './components/common/announcement/NotificationSnackbar.vue';

// screens which show the tax year / fiscal year in the app bar
const TAX_ROUTE_PREFIXES = ['/tax/', '/apportionment'];

export default {
  name: 'App',

  components: {
    ProgramList,
    ReAuth,
    Snackbar,
    Footer,
    UpdateUserPassword,
    InputDialogForWorkstationId,
    NotificationBadge,
    NotificationSnackbar,
  },
  data: () => ({
    drawer: false,
    group: null,
    // localStorage isn't reactive, so it's mirrored here (see the $route watcher)
    wkstBusinessDate: getBusinessDateForPaymentEntry(),
  }),
  created() {
    this.initOrRehydrateState(); // async fire and forget
    window.addEventListener('keydown', this.globalShortcutHandler);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.globalShortcutHandler);
  },
  methods: {
    ...mapActions('enums', [
      'cacheEnums',
    ]),
    ...mapActions('SystemConfig', [
      'loadConfig',
    ]),
    ...mapMutations('auth/token', [
      'rehydrateLogin',
    ]),
    ...mapMutations([
      'setPrintScreen',
    ]),
    // things in state which should be re-set after a page reload
    // (or on initial load of the page) can go here globally
    // items of note: localStorage
    //   localStorage needs to be duplicated in state, so that it's reactive
    async initOrRehydrateState() {
      this.cacheEnums();
      this.rehydrateLogin();
      this.loadConfig({ requiredScope: 'global' });
      /* For isTestEnvironment flag */
      this.loadConfig({ requiredScope: 'devConfig' });
      // Todo - cleanup#od-bank-and-shared-bank
      /* For OD-bansk & Shared Bank Migration */
      this.loadConfig({ requiredScope: 'odconfig' });
      /* For the 'changeBussDateIndividually' flag behind the app bar's Fiscal Year */
      this.loadConfig({ requiredScope: 'paymentConfig' });
    },
    globalShortcutHandler(e) {
      // console.log(JSON.stringify(e, null, 1));
      if (e.key === '+') {
        // simulate 'tab' (full keyboard number pad convenience feature for TM Consulting)
        // doesn't work - security in modern browsers does not allow it
        // Nehemiah found a way to use autohotkey instead
        // e.target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Tab' }));
      }
    },
    printScreen(print) {
      this.setPrintScreen(print);
      this.$nextTick(() => {
        window.print();
        this.$nextTick(() => {
          this.setPrintScreen(false);
        });
      });
    },
  },
  computed: {
    ...mapState({
      isPrintScreen: (state) => state.isPrintScreen,
      wkst: (state) => state.SystemConfig.workStationItem.wkst,
      // Added this for Global title
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem,
      latestTaxYear: (state) => state.tax.taxCommon.latestTaxYear,
    }),
    // tax inquiry, payments, adjustments, special tax items, mortgage co,
    // delinquent tax & apportionment all live under these prefixes
    isTaxScreen() {
      const { path } = this.$route;
      return TAX_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
    },
    // the business date for payment entry - kept globally, or per workstation in
    // localStorage when 'changeBussDateIndividually' is on. Same precedence as
    // initCriteria in components/tax/payments/paymentTransactionList.vue
    businessDate() {
      const { changeBussDateIndividually } = this.paymentSettingItem || {};
      const wkstBussDate = changeBussDateIndividually ? this.wkstBusinessDate : '';
      const bussDate = wkstBussDate || (this.globalSettingItem || {}).businessDate;
      // toIsoDate, not toShortDate: 'MM-DD-YYYY' is parsed as LOCAL midnight while
      // toFiscalYear compares UTC dates, which loses a year on July 1 in UTC+ zones
      return bussDate ? toIsoDate(bussDate) : toIsoDate();
    },
    headerTaxYear() {
      if (!this.isTaxScreen) return '';
      return this.latestTaxYear || '';
    },
    // the FY the county is posting into - NOT a per-screen search filter, so that
    // every tax screen agrees on it
    headerFiscalYear() {
      if (!this.isTaxScreen) return '';
      return toFiscalYear(new Date(this.businessDate)) || '';
    },
    reportMode() {
      const { path } = this.$route;
      // If required any report mode please add '/reports' at pre route
      return (path.indexOf('reports/') > -1);
    },
    // *************Added this function for Global title************
    globalTitle() {
      const { globalTitle } = this.globalSettingItem || {};
      return globalTitle || '';
    },
  },
  watch: {
    group() {
      this.drawer = false;
    },
    // payment entry saves the per workstation business date to localStorage, which
    // can't be watched - pick up any change as soon as the user moves screens
    $route() {
      this.wkstBusinessDate = getBusinessDateForPaymentEntry();
    },
  },
};
</script>
<style>
.workstation-number {
  font-weight: bold;
  color: #ffa500;
}
.header-years {
  font-size: 0.8rem;
  font-weight: bold;
  color: #fff;
}
.global-title{
  font-size: 1.8rem;
  color: #fff;
  display: flex;
  align-items: center;
  margin: auto;
};
.app-bar-class.v-app-bar.v-app-bar--fixed{
  z-index: 111 !important;
}

@media print {
  /* Hide navigation chrome — app bar and drawer cause multi-page print */
  .v-app-bar,
  .v-navigation-drawer,
  /* Hide only the overlay scrim (dark backdrop); __content holds the dialog */
  .v-overlay__scrim {
    display: none !important;
  }

  /* Vuetify sets min-height: 100vh on this container which forces the app to
     span multiple print pages even when most content is hidden */
  .v-application--wrap {
    min-height: 0 !important;
  }

  /* When a dialog is open (.v-dialog__content--active is present), hide the
     background page content so only the dialog prints. Uses CSS :has()
     (Chrome 105+). The overflow-y-hidden hook Vuetify documents is NOT
     added by this app's dialog components. */
  body:has(.v-dialog__content--active) .v-main,
  body:has(.v-dialog__content--active) footer {
    display: none !important;
  }
}
</style>
